import type { AxiosInstance } from 'axios'
import { wrapApiCall } from '@/lib/axios'
import type { Inquiry, InquiryStatus, MarketplaceItem, Partner } from './model'

export interface MarketplaceItemsQuery {
  query?: string
  type?: string
  category?: string
  partnerId?: string
  page?: number
  pageSize?: number
}

export interface MarketplaceItemsResponse {
  data: MarketplaceItem[]
  pagination: { total: number; page: number; pageSize: number }
}

export interface CreateInquiryBody {
  partnerId: string
  clubName?: string
  note?: string
  items: { itemId: string; quantity: number }[]
  contact: {
    contactName: string
    phone: string
    email: string
    preferredDate?: string
  }
}

/**
 * CRM-facing Partner Marketplace API (fitup-backend `/marketplace/*`).
 * The axios instance already injects the Firebase token + x-tenant-id /
 * branch-id / platform headers, so the backend scopes everything per tenant.
 */
export const MarketplaceService = (client: AxiosInstance) => {
  return {
    async getPartners(): Promise<Partner[]> {
      const { data } = await wrapApiCall(() => client.get<Partner[]>('/marketplace/partners'))
      return data
    },

    async getItems(params: MarketplaceItemsQuery): Promise<MarketplaceItemsResponse> {
      const { data } = await wrapApiCall(() =>
        client.get<MarketplaceItemsResponse>('/marketplace/items', { params })
      )
      return data
    },

    async getItem(id: string): Promise<MarketplaceItem> {
      const { data } = await wrapApiCall(() =>
        client.get<MarketplaceItem>(`/marketplace/items/${id}`)
      )
      return data
    },

    async getInquiries(): Promise<Inquiry[]> {
      const { data } = await wrapApiCall(() => client.get<Inquiry[]>('/marketplace/inquiries'))
      return data
    },

    async getInquiry(id: string): Promise<Inquiry> {
      const { data } = await wrapApiCall(() => client.get<Inquiry>(`/marketplace/inquiries/${id}`))
      return data
    },

    async createInquiry(body: CreateInquiryBody): Promise<Inquiry> {
      const { data } = await wrapApiCall(() => client.post<Inquiry>('/marketplace/inquiries', body))
      return data
    },

    async updateInquiryStatus(id: string, status: InquiryStatus): Promise<Inquiry> {
      const { data } = await wrapApiCall(() =>
        client.patch<Inquiry>(`/marketplace/inquiries/${id}/status`, { status })
      )
      return data
    },
  }
}
